/** @jsxImportSource @emotion/react */

import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import {
  AGE_GROUP_QUESTION,
  AGE_GROUP_VOLUNTEER_QUESTION,
  AGE_QUESTION,
  GEOLOCATION_QUESTION,
  MULTI_OPTIONAL_QUESTION,
  MULTI_QUESTION,
  POSTCODE_QUESTION,
  SINGLE_QUESTION,
  SLIDER_QUESTION,
} from '../../const';
import { useOnboardingDataContext } from '../../context/OnboardingData';
import { useRecommendations } from '../../context/recommendations';
import useMediaQuery from '../../hooks/useMediaQuery';
import AgeGroupQuestion from '../AgeGroupQuestion';
import AgeQuestion from '../AgeQuestion';
import ExternalLink from '../ExternalLink';
import GeolocationQuestion from '../GeolocationQuestion';
import { DisabledDownArrow, DownArrow, UpArrow } from '../Icons';
import MultipleOptionalQuestion from '../MultipleOptionalQuestion';
import MultipleQuestion from '../MultipleQuestion';
import {
  ageHeaderTitle,
  genderHeaderTitle,
} from '../OnboardingModal/MiniOnboarding';
import PostcodeQuestion from '../PostcodeQuestion';
import SingleQuestion from '../SingleQuestion';
import SliderQuestion from '../SliderQuestion';
import Text from '../Text';
import useStyles from './styles';

const Questions = {
  [SINGLE_QUESTION]: SingleQuestion,
  [MULTI_OPTIONAL_QUESTION]: MultipleOptionalQuestion,
  [MULTI_QUESTION]: MultipleQuestion,
  [SLIDER_QUESTION]: SliderQuestion,
  [GEOLOCATION_QUESTION]: GeolocationQuestion,
  [POSTCODE_QUESTION]: PostcodeQuestion,
  [AGE_QUESTION]: AgeQuestion,
  [AGE_GROUP_QUESTION]: AgeGroupQuestion,
  [AGE_GROUP_VOLUNTEER_QUESTION]: AgeGroupQuestion,
};

const Question = ({
  type,
  title,
  questionOrder,
  helpText,
  totalSteps,
  infoLink,
  selectedAge,
  selectedGender,
  helpTextSecondary,
  onClickFunction,
  onAnswer,
  disabilityCheck,
  quickSearch,
  isVisible,
  ...rest
}) => {
  const titleRef = useRef();
  const styles = useStyles();
  const [headerTitle, setHeaderTitle] = useState();
  const [nextLinkCta, setNextLinkCta] = useState(false);
  const questionTitle = title;
  const answerRef = useRef();
  const genderRef = useRef();
  const locationRef = useRef();
  const postcodeRef = useRef();
  const disabilityRef = useRef();
  const { arrowContext, selectedAgeUser, setArrowFunction } =
    useOnboardingDataContext();
  const { backlinkcta } = useRecommendations();
  // const isDesktop = useMediaQuery('screen and (min-width: 1196px)');

  const isIpad = useMediaQuery('screen and (min-width: 996px)');
  const isMobile = useMediaQuery(
    'screen and (min-width: 760px) and (max-width: 996px)'
  );
  const isSmallMobile = useMediaQuery('screen and (max-width: 760px)');

  const [bothAnswer, setBothAnswer] = useState(false);
  const [prevAnswer, setPrevAnswer] = useState(false);
  const [notselect, setNotselectAnswer] = useState();
  const [selectedAnswer, setSelectedAnswer] = useState();

  const setBothAnswerTrueFalse = bothVal => {
    setBothAnswer(bothVal);
  };

  const setPrevAnswerIdNull = val => {
    setPrevAnswer(val);
  };

  const setNotselectAnswerTrueFalse = val => {
    setNotselectAnswer(val);
  };

  const setSelectedAnswerTrueFalse = val => {
    setSelectedAnswer(val);
  };

  const nextClick = () => {
    setTimeout(() => {
      setNextLinkCta(false);
    }, 2000);
    setNextLinkCta(!nextLinkCta);
  };

  useEffect(() => {
    setHeaderTitle(
      !selectedAge
        ? ageHeaderTitle(questionTitle)
        : !selectedGender && questionOrder === 3
        ? genderHeaderTitle(questionTitle)
        : questionOrder === 6 &&
          JSON.parse(sessionStorage.getItem('ISVOLUNTEER'))
        ? 'Would you like to volunteer in a disability team?'
        : questionTitle
    );
  }, [questionTitle, selectedAge, selectedGender, questionOrder]);

  // when the question is changed we want to focus on the new one
  useLayoutEffect(() => {
    setTimeout(() => {
      titleRef.current?.focus({ preventScroll: true });
    }, 200);
  }, [rest.id]);

  if (!type) {
    return null;
  }

  const CurrentQuestion = Questions[type];

  const addOnClick = () => {
    if (questionOrder === 1) {
      return;
    }
    onClickFunction();
    setBothAnswerTrueFalse(false);
    setPrevAnswerIdNull(null);
  };

  const handleKeyDown = (e, action) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (typeof action === 'function') {
        action();
      }
    }
  };

  // Define the 'next' action logic as a single function to pass to handleKeyDown
  const handleNextAction = () => {
    nextClick();
    setArrowFunction(false);
    // Logic is identical for both iPad and mobile next actions
    if (questionOrder === 1) {
      answerRef.current?.handleNextNav(
        selectedAgeUser,
        rest?.readableId,
        ageRanges,
        onAnswer
      );
    } else if (questionOrder === 6) {
      disabilityRef.current?.disabilityOption();
    } else if (questionOrder === 7) {
      locationRef.current?.selectLocationAnswer();
    } else if (questionOrder === 8) {
      postcodeRef.current?.selectpostcodeAnswer();
    } else {
      genderRef.current?.selectedGenderAnswer();
    }
    setBothAnswerTrueFalse(false);
    setNotselectAnswerTrueFalse(false);
    setSelectedAnswerTrueFalse(null);
    setPrevAnswerIdNull(null);
  };

  const ageRanges = [
    {
      min: 5,
      max: 17,
      nextQuestionId: 'd9572be5-72f2-42af-87ea-a3298c6eacd7',
    },
    {
      min: 18,
      max: 99,
      nextQuestionId: 'd43569c8-1a01-4c53-9575-128698905fbc',
    },
  ];
  const refPreference =
    questionOrder === 1
      ? answerRef
      : questionOrder === 6
      ? disabilityRef
      : questionOrder === 7
      ? locationRef
      : questionOrder === 8
      ? postcodeRef
      : genderRef;
  // may be we can use it for mini-onboarding
  // const selectedQuestion = getSelectedQuestion();

  // Note: the existing code uses `arrowContext` to decide if the next arrow is active,
  // which might conflict with a simple `isLastQuestion` check if the final question
  // isn't answered yet. We primarily use the existing logic flow but ensure
  // accessibility attributes reflect the interactive state.
  
  // Determine which questionOrder is the "first visible" one depending on quickSearch.
  // In quickSearch mode the visible first question might be order 7; otherwise it's 1.
  const firstVisibleQuestionOrder = quickSearch ? 7: 1;
  const isPrevDisabled = questionOrder === firstVisibleQuestionOrder;

  return (
    <div css={styles.base(backlinkcta, nextLinkCta, questionOrder)}>

      <div css={styles.answers(questionOrder)}>
        <fieldset css={styles.fieldset}>
          <legend>{questionTitle}</legend>
          <CurrentQuestion
            ref={refPreference}
            selectedAge={selectedAge}
            questionOrder={questionOrder}
            headerTitle={headerTitle}
            nextClickAnswer={nextClick}
            onAnswer={onAnswer}
            setBothAnswerTrueFalse={setBothAnswerTrueFalse}
            bothAnswer={bothAnswer}
            setPrevAnswerIdNull={setPrevAnswerIdNull}
            prevAnswer={prevAnswer}
            setNotselectAnswerTrueFalse={setNotselectAnswerTrueFalse}
            notselect={notselect}
            setSelectedAnswerTrueFalse={setSelectedAnswerTrueFalse}
            selectedAnswer={selectedAnswer}
            isVisible={isVisible}
            {...rest}
          />
        </fieldset>
      </div>
      <div css={styles.divWidth}>
        {(helpText || disabilityCheck) && !quickSearch && (
          <Text css={[styles.helpText(rest?.readableId), styles.helpquestion]}>
            QUESTION 0
            {questionOrder === 6
              ? '5'
              : questionOrder === 7
              ? '6'
              : questionOrder === 8
              ? '6'
              : questionOrder}
            /06
          </Text>
        )}
        <Text
          as="h1"
          size="2"
          id={rest.readableId}
          css={styles.title(rest?.readableId)}
          ref={titleRef}
          tabIndex={-1}
        >
          {headerTitle}
        </Text>
        {helpText && (
          <Text css={styles.helpText(rest?.readableId)}>
            {selectedAge &&
            !selectedGender &&
            questionOrder === 3 &&
            JSON.parse(sessionStorage.getItem('ISVOLUNTEER'))
              ? 'Please choose the gender(s) that best suits you'
              : questionOrder === 1
              ? isIpad
                ? helpText
                : 'Type the players’ age below.'
              : helpText}
          </Text>
        )}
        {helpTextSecondary && (
          <Text css={[styles.helpText, styles.helpSecText]}>
            {helpTextSecondary}
          </Text>
        )}
        {isIpad && (
          <div css={styles.spacingDiv}>
            {/* Previous Question Arrow */}
            <div
              css={styles.down(arrowContext, questionOrder, quickSearch)}
              onClick={isPrevDisabled ? null : addOnClick}
              onKeyDown={e => handleKeyDown(e, isPrevDisabled ? null : addOnClick)}
              aria-label="Previous Question"
              tabIndex={isPrevDisabled ? -1 : 0}
              role="button"
              aria-disabled={isPrevDisabled}
            >
              {isPrevDisabled ? <DisabledDownArrow /> : <UpArrow />}
            </div>
            {/* Next Question Arrow */}
            <div
              css={styles.up(arrowContext)}
              onClick={arrowContext === false ? null : handleNextAction}
              onKeyDown={e =>
                handleKeyDown(e, arrowContext === false ? null : handleNextAction)
              }
              aria-label="Next Question"
              tabIndex={arrowContext === false ? -1 : 0}
              role={arrowContext === false ? undefined : 'button'}
            >
              {arrowContext === true ? <DownArrow /> : <DisabledDownArrow />}
            </div>
          </div>
        )}
      </div>
      {(isMobile || isSmallMobile) && (
        <div css={styles.spacingDivMob(isIpad, isSmallMobile, isMobile)}>
          {/* Previous Question Arrow (Mobile) */}
          <div
            css={styles.down(arrowContext, questionOrder, quickSearch)}
            onClick={isPrevDisabled ? null : addOnClick}
            onKeyDown={e => handleKeyDown(e, isPrevDisabled ? null : addOnClick)}
            aria-label="Previous Question"
            tabIndex={isPrevDisabled ? -1 : 0}
            role="button"
            aria-disabled={isPrevDisabled}
          >
            {isPrevDisabled ? <DisabledDownArrow /> : <UpArrow />}
          </div>
          {/* Next Question Arrow (Mobile) */}
          <div
            css={styles.up(arrowContext)}
            aria-label="Next Question"
            onClick={arrowContext === false ? null : handleNextAction}
            onKeyDown={e =>
              handleKeyDown(e, arrowContext === false ? null : handleNextAction)
            }
            tabIndex={arrowContext === false ? -1 : 0}
            role={arrowContext === false ? undefined : 'button'}
          >
            {arrowContext === true ? <DownArrow /> : <DisabledDownArrow />}
          </div>
        </div>
      )}

      {infoLink && infoLink.href && (
        <ExternalLink css={styles.infoLink} href={infoLink.href}>
          {infoLink.text}
        </ExternalLink>
      )}
    </div>
  );
};

export default Question;